/* istanbul ignore file */
"use client";

import { useState, useMemo, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "@/core/redux/store";
import {
  toggleSidebarFilterStatus,
  toggleSidebarFilterConflicts,
  clearSidebarFilter,
} from "@/core/redux/slices/specification";
import { SpecificationItemAnswerStatus } from "@/shared/enums/specification-item-answer";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/shared/components/ui/popover";
import { Checkbox } from "@/shared/components/ui/checkbox";
import {
  Check,
  ChevronRight,
  FileText,
  ListFilter,
  MessageSquareDashed,
  Sparkles,
  SquareCheckBig,
} from "lucide-react";
import YesIcon from "@/assets/icons/yes.svg";
import NoIcon from "@/assets/icons/no.svg";
import BelkiIcon from "@/assets/icons/belki.svg";
import BulunamadiIcon from "@/assets/icons/bulunamadi.svg";
import CakisanIcon from "@/assets/icons/cakisan.svg";
import { cn } from "@/lib/utils";
import {
  ISurveyAnswer,
  ISupplierCategory,
  ISupplierCategoryItem,
} from "@/shared/services/interface/responses/suplier-response";
import { getAllItems } from "@/core/redux/helper/specification";

const normalizeAnswerStatus = (
  status: unknown
): SpecificationItemAnswerStatus | null => {
  if (typeof status === "number") {
    if (
      status === SpecificationItemAnswerStatus.SEARCHING ||
      status === SpecificationItemAnswerStatus.ANSWERED_PARTIALLY ||
      status === SpecificationItemAnswerStatus.ANSWERED_YES ||
      status === SpecificationItemAnswerStatus.ANSWERED_NO ||
      status === SpecificationItemAnswerStatus.NOT_FOUND
    ) {
      return status;
    }
    return null;
  }

  if (typeof status === "string") {
    const trimmed = status.trim();
    if (!trimmed) return null;
    const asNumber = Number(trimmed);
    if (!Number.isNaN(asNumber)) return normalizeAnswerStatus(asNumber);

    switch (trimmed.toUpperCase()) {
      case "SEARCHING":
        return SpecificationItemAnswerStatus.SEARCHING;
      case "ANSWERED_PARTIALLY":
      case "PARTIALLY":
        return SpecificationItemAnswerStatus.ANSWERED_PARTIALLY;
      case "ANSWERED_YES":
      case "YES":
        return SpecificationItemAnswerStatus.ANSWERED_YES;
      case "ANSWERED_NO":
      case "NO":
        return SpecificationItemAnswerStatus.ANSWERED_NO;
      case "NOT_FOUND":
      case "NOTFOUND":
        return SpecificationItemAnswerStatus.NOT_FOUND;
      default:
        return null;
    }
  }

  return null;
};

const onRowKeyDown = (e: React.KeyboardEvent, action: () => void) => {
  if (e.key === "Enter" || e.key === " ") {
    e.preventDefault();
    action();
  }
};

// Helper to count items by answer status
const findSurveyAnswerForItem = (
  categories: ISupplierCategory[] | undefined,
  itemId: string
): ISurveyAnswer | null => {
  if (!categories) return null;
  for (const category of categories) {
    for (const child of category.children) {
      if (child.type === "item" && child.id === itemId) {
        return (child as ISupplierCategoryItem).surveyAnswer;
      }
      if (child.type === "category") {
        const nested = findSurveyAnswerForItem(
          [child as ISupplierCategory],
          itemId
        );
        if (nested !== null) return nested;
      }
    }
  }
  return null;
};

export function SidebarFilterDropdown() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [statusSubOpen, setStatusSubOpen] = useState(false);

  const sidebarFilter = useSelector(
    (state) => state.specification.sidebarFilter
  );
  const filteredCategories = useSelector(
    (state) => state.specification.filteredCategories
  );
  const supplierCategoriesResponse = useSelector(
    (state) => state.productAnswer.supplierCategoriesResponse
  );
  const supplierSurveys = useSelector(
    (state) =>
      state.productAnswer.supplierAndAIAnswersResponse?.supplierSurveys
  );
  const aiAnswers = useSelector(
    (state) =>
      state.productAnswer.supplierAndAIAnswersResponse
        ?.specificationItemAnswers
  );
  const transientSelectedAnswer = useSelector(
    (state) => state.productAnswer.transientSelectedAnswer
  );

  // Compute counts for each status
  const counts = useMemo(() => {
    const allItems = getAllItems(filteredCategories);
    let total = allItems.length;
    let yes = 0;
    let no = 0;
    let partially = 0;
    let notFound = 0;
    let conflicts = 0;

    for (const item of allItems) {
      // Get survey answer (same logic as category-item)
      let surveyAnswer: ISurveyAnswer | null = null;
      if (transientSelectedAnswer?.specificationItemId === item.id) {
        surveyAnswer = {
          status: transientSelectedAnswer.status,
          description: "",
        };
      } else {
        const fromEndpoint = supplierSurveys?.find(
          (s) => s.specificationItemId === item.id
        );
        if (fromEndpoint) {
          surveyAnswer = {
            status: fromEndpoint.status,
            description: fromEndpoint.description ?? "",
          };
        } else {
          surveyAnswer = findSurveyAnswerForItem(
            supplierCategoriesResponse?.categories,
            item.id
          );
        }
      }

      if (surveyAnswer) {
        const status = normalizeAnswerStatus(surveyAnswer.status);
        if (status === SpecificationItemAnswerStatus.ANSWERED_YES) yes++;
        if (status === SpecificationItemAnswerStatus.ANSWERED_NO) no++;
        if (status === SpecificationItemAnswerStatus.ANSWERED_PARTIALLY)
          partially++;
        if (status === SpecificationItemAnswerStatus.NOT_FOUND) notFound++;

        // Check conflict
        if (aiAnswers) {
          const aiAnswer = aiAnswers.find(
            (a) => a.specificationItemId === item.id
          );
          if (aiAnswer) {
            const answered = [
              SpecificationItemAnswerStatus.ANSWERED_YES,
              SpecificationItemAnswerStatus.ANSWERED_NO,
              SpecificationItemAnswerStatus.ANSWERED_PARTIALLY,
            ];
            const aiStatus = normalizeAnswerStatus(aiAnswer.status);
            if (
              status !== null &&
              aiStatus !== null &&
              answered.includes(status) &&
              answered.includes(aiStatus) &&
              status !== aiStatus
            ) {
              conflicts++;
            }
          }
        }
      }
    }

    return { total, yes, no, partially, notFound, conflicts };
  }, [
    filteredCategories,
    supplierCategoriesResponse,
    supplierSurveys,
    aiAnswers,
    transientSelectedAnswer,
  ]);

  const hasActiveFilter =
    sidebarFilter.answerStatuses.length > 0 || sidebarFilter.showConflicts;

  const handleToggleStatus = useCallback(
    (status: SpecificationItemAnswerStatus) => {
      dispatch(toggleSidebarFilterStatus(status));
    },
    [dispatch]
  );

  const handleToggleConflicts = useCallback(() => {
    dispatch(toggleSidebarFilterConflicts());
  }, [dispatch]);

  const statusItems = [
    {
      status: SpecificationItemAnswerStatus.ANSWERED_YES,
      label: t("filter-yes"),
      count: counts.yes,
      icon: <img src={YesIcon} alt="yes" width={14} height={14} className="flex-shrink-0" />,
    },
    {
      status: SpecificationItemAnswerStatus.ANSWERED_NO,
      label: t("filter-no"),
      count: counts.no,
      icon: <img src={NoIcon} alt="no" width={14} height={14} className="flex-shrink-0" />,
    },
    {
      status: SpecificationItemAnswerStatus.ANSWERED_PARTIALLY,
      label: t("filter-partially"),
      count: counts.partially,
      icon: <img src={BelkiIcon} alt="partially" width={14} height={14} className="flex-shrink-0" />,
    },
    {
      status: SpecificationItemAnswerStatus.NOT_FOUND,
      label: t("filter-not-found"),
      count: counts.notFound,
      icon: <img src={BulunamadiIcon} alt="not found" width={14} height={14} className="flex-shrink-0" />,
    },
  ];

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          className={cn(
            "flex items-center justify-center w-[36px] h-[36px] rounded-md bg-transparent hover:bg-[#E4E4E7] transition-colors flex-shrink-0",
            hasActiveFilter && "bg-[#E4E4E7]"
          )}
          aria-label={t("filter")}
        >
          <ListFilter size={16} className="text-zinc-700" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-[245px] p-1 rounded-lg"
      >
        {/* AI ile cevaplanan */}
        <button className="flex items-center justify-between w-full px-3 py-2 text-sm text-left rounded-md hover:bg-zinc-100 transition-colors">
          <div className="flex items-center gap-2">
            <Sparkles
              size={16}
              className="text-zinc-700 flex-shrink-0"
            />
            <span className="flex-1 text-left text-xs text-zinc-700">
              {t("filter-answered-with-ai")}
            </span>
          </div>
          <Check size={14} className="text-zinc-700" />
        </button>

        {/* Döküman ile cevaplanan */}
        <button className="flex items-center justify-between w-full px-3 py-2 text-sm text-left rounded-md hover:bg-zinc-100 transition-colors">
          <div className="flex items-center gap-2">
            <FileText size={16} className="text-zinc-700 flex-shrink-0" />
            <span className="flex-1 text-left text-xs text-zinc-700">
              {t("filter-answered-with-document")}
            </span>
          </div>
        </button>

        {/* Tamamlanan */}
        <button className="flex items-center justify-between w-full px-3 py-2 text-sm text-left rounded-md hover:bg-zinc-100 transition-colors">
          <div className="flex items-center gap-2">
            <SquareCheckBig size={16} className="text-zinc-700 flex-shrink-0" />
            <span className="flex-1 text-left text-xs text-zinc-700">
              {t("filter-completed")}
            </span>
          </div>
        </button>

        {/* Answer Status - expands to sub-menu */}
        <Popover open={statusSubOpen} onOpenChange={setStatusSubOpen}>
          <PopoverTrigger asChild>
            <button className="flex items-center justify-between w-full px-3 py-2 text-sm text-left rounded-md hover:bg-zinc-100 transition-colors">
              <div className="flex items-center gap-2">
                <MessageSquareDashed
                  size={16}
                  className="text-zinc-700 flex-shrink-0"
                />
                <span className="flex-1 text-left text-xs text-zinc-700">
                  {t("filter-answer-status")}
                </span>
              </div>
              <ChevronRight size={14} className="text-zinc-400" />
            </button>
          </PopoverTrigger>
          <PopoverContent
            side="right"
            align="start"
            sideOffset={4}
            className="w-[200px] p-1 rounded-lg"
          >
            {/* All (not a <button> to avoid nesting a Radix Checkbox button) */}
            <div
              role="button"
              tabIndex={0}
              className="flex items-center justify-between w-full px-3 py-2 text-sm text-left rounded-md hover:bg-zinc-100 transition-colors cursor-pointer select-none"
              onKeyDown={(e) =>
                onRowKeyDown(e, () => {
                  if (hasActiveFilter) dispatch(clearSidebarFilter());
                })
              }
              onClick={() => {
                if (hasActiveFilter) dispatch(clearSidebarFilter());
              }}
            >
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={!hasActiveFilter}
                  className="h-[18px] w-[18px] rounded-[4px]"
                  onClick={(e) => e.stopPropagation()}
                  onCheckedChange={() => {
                    if (hasActiveFilter) {
                      dispatch(clearSidebarFilter());
                    }
                  }}
                />
                <span className="flex-1 text-left text-xs text-zinc-700 font-400">
                  {t("filter-all")}
                </span>
              </div>
              <span className="text-zinc-400 text-xs">{counts.total}</span>
            </div>

            {/* Status items */}
            {statusItems.map((item) => (
              <div
                key={item.status}
                role="button"
                tabIndex={0}
                className="flex items-center justify-between w-full px-3 py-2 text-sm text-left rounded-md hover:bg-zinc-100 transition-colors cursor-pointer select-none"
                onKeyDown={(e) =>
                  onRowKeyDown(e, () => handleToggleStatus(item.status))
                }
                onClick={() => handleToggleStatus(item.status)}
              >
                <div className="flex items-center gap-2">
                  <Checkbox
                    checked={sidebarFilter.answerStatuses.includes(item.status)}
                    className="h-[18px] w-[18px] rounded-[4px]"
                    onClick={(e) => e.stopPropagation()}
                    onCheckedChange={() => handleToggleStatus(item.status)}
                  />
                  {item.icon}
                  <span className="flex-1 text-left text-zinc-700">
                    {item.label}
                  </span>
                </div>
                <span className="text-zinc-400 text-xs">{item.count}</span>
              </div>
            ))}

            {/* Conflicts */}
            <div
              role="button"
              tabIndex={0}
              className="flex items-center justify-between w-full px-3 py-2 text-sm text-left rounded-md hover:bg-zinc-100 transition-colors cursor-pointer select-none"
              onKeyDown={(e) => onRowKeyDown(e, handleToggleConflicts)}
              onClick={handleToggleConflicts}
            >
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={sidebarFilter.showConflicts}
                  className="h-[18px] w-[18px] rounded-[4px]"
                  onClick={(e) => e.stopPropagation()}
                  onCheckedChange={handleToggleConflicts}
                />
                <img
                  src={CakisanIcon}
                  alt="conflict"
                  width={14}
                  height={14}
                />
                <span className="flex-1 text-left text-zinc-700">
                  {t("filter-conflicts")}
                </span>
              </div>
              <span className="text-zinc-400 text-xs">{counts.conflicts}</span>
            </div>
          </PopoverContent>
        </Popover>
      </PopoverContent>
    </Popover>
  );
}
